import { Injectable, inject } from '@angular/core';
import { getAuth } from 'firebase/auth';
import { AuthBridge } from '@crewdle/auth-bridge';
import { WhiteLabelService } from '@crewdle/white-label/angular';
import { environment } from 'src/environments/environment';

/**
 * Holds the OAuth-style bridge to the centralized auth service
 * (auth.crewdle.ai). Lazily constructed so Firebase is initialized before
 * getAuth() is called.
 *
 * On a partner custom domain the bridge targets the partner's own auth
 * origin (an isolated SSO realm), resolved by the white-label service from
 * this hostname's `domains/{hostname}` doc; crewdle.ai domains keep the
 * environment default.
 */
@Injectable({ providedIn: 'root' })
export class AuthBridgeService {
  private readonly whiteLabel = inject(WhiteLabelService);

  private instance?: AuthBridge;
  private sessionReady?: Promise<unknown>;

  get bridge(): AuthBridge {
    if (!this.instance) {
      this.instance = new AuthBridge({
        auth: getAuth(),
        authBaseUrl: () => this.whiteLabel.authBaseUrl(environment.authBaseUrl),
      });
    }
    return this.instance;
  }

  /**
   * Resolve the centralized session exactly once per page load.
   *
   * Angular runs a route's `canActivate` guards CONCURRENTLY, so AuthGuard and
   * RoleGuard (+ requireProductAccess) fire at the same time. AuthGuard is the
   * one that exchanges a returning `?code` and signs in via signInWithCustomToken
   * — until that finishes, `auth.currentUser` is null, so any other guard that
   * reads the user races ahead and sees nothing ("User not found").
   *
   * Every guard awaits this shared, memoized promise so the (single-use) code is
   * exchanged exactly once and they all wait for the session before touching
   * auth. Memoizing for the service lifetime is safe: login()/signOutEverywhere()
   * both do full-page navigations, so the singleton is recreated on return.
   */
  ensureReady(): Promise<unknown> {
    if (!this.sessionReady) {
      this.sessionReady = this.bridge.ensureSession().catch(() => undefined);
    }
    return this.sessionReady;
  }
}
